import styled from '@emotion/styled';
import { useMatches } from 'react-router-dom';
import { useLingui } from '@lingui/react';
import type { MessageDescriptor } from '@lingui/core';
import { Text } from 'components/text/Text';
import { useOrgType } from 'features/accessControl';
import { UserDetails } from './UserDetails';
import { OldSiteLink } from './OldSiteLink';

interface RouteHandle {
  title?: MessageDescriptor;
  supplierTitle?: MessageDescriptor;
}

function isRouteHandle(handle: unknown): handle is RouteHandle {
  return typeof handle === 'object' && handle !== null && 'title' in handle;
}

function usePageTitle(): string | undefined {
  const { i18n } = useLingui();
  const matches = useMatches();
  const isSupplier = useOrgType(2);
  const match = matches
    .slice()
    .reverse()
    .find(m => isRouteHandle(m.handle) && m.handle.title);
  const handle = isRouteHandle(match?.handle) ? match.handle : undefined;
  if (!handle?.title) return undefined;
  if (isSupplier && handle.supplierTitle) {
    return i18n._(handle.supplierTitle);
  }
  return i18n._(handle.title);
}

export const Topbar = () => {
  const title = usePageTitle();

  return (
    <Container>
      <LeftBar>
        {title && (
          <Text variant="headlineLg" weight="semiBold">
            {title}
          </Text>
        )}
      </LeftBar>
      <RightBar>
        <OldSiteLink />
        <UserDetails />
      </RightBar>
    </Container>
  );
};

const Container = styled.header`
  display: flex;
  position: relative;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  height: 88px;
  background-color: ${p => p.theme.palette.bg.secondary};
  padding: 24px;
  align-items: center;
  gap: 24px;
`;

const RightBar = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  flex-grow: 1;
  flex-basis: 0;
  justify-content: flex-end;
  align-items: center;
`;

const LeftBar = styled.div`
  flex-grow: 1;
  flex-basis: 0;
  display: flex;
  align-items: center;
`;
